import { useEffect, useState, useCallback, useRef } from "react";
import PopoutTextarea from "@/components/PopoutTextarea";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { newEducation, downloadBlob } from "@/lib/resumeUtils";
import { sortExperiences } from "@/lib/experienceSort";
import ExperienceList from "./ExperienceList";
import ProfileWizard from "./ProfileWizard";
import AiGeneratedInfoPanel from "./AiGeneratedInfoPanel";
import { PRESET_DESIGNS, PRESET_KEYS, PRESET_TEMPLATES } from "@/components/templates/TemplateDesigner";
import { FileDown, Sparkles, Plus, X, Check, Eye, EyeOff, RefreshCw, ArrowLeft, PenLine } from "lucide-react";
import { toast } from "sonner";

const PERSONAL_FIELDS = [
  ["full_name", "rbFullName"], ["title", "rbJobTitle"],
];

// Which centrally managed personalia can be shown/hidden per CV
const PERSONAL_TOGGLE_FIELDS = [
  ["address", "rbAddress"], ["birthdate", "ppBirthdate"], ["nationality", "rbNationality"],
  ["phone", "rbPhone"], ["email", "rbEmail"], ["linkedin", "rbLinkedin"],
];
const ALL_PERSONAL_KEYS = PERSONAL_TOGGLE_FIELDS.map(([k]) => k);

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId}>
      <p className="ed-eyebrow">{title}</p>
      <div className="ed-rule w-16 my-3" />
      {children}
    </section>
  );
}

export default function ResumeEditor({ resumeId, onChanged, onClose }) {
  const { t, lang: userLang } = useT();
  const [r, setR] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [profileWizard, setProfileWizard] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [hobbyInput, setHobbyInput] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [appliedTplId, setAppliedTplId] = useState(null);
  const [mobilePane, setMobilePane] = useState("edit");
  const [pdfLang, setPdfLang] = useState(userLang || "nl");
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  // Profile data — used to filter which personalia chips are meaningful to
  // toggle: showing "GEBOORTEDATUM" when the user hasn't entered a birthdate
  // is confusing, so we hide chips whose underlying value is empty.
  const [profileData, setProfileData] = useState(null);
  // Approved references pool — populates the multi-select dropdown in
  // the "Referenties" section. When null → still loading.
  const [availableRefs, setAvailableRefs] = useState(null);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  const rRef = useRef(null);
  rRef.current = r;

  const loadTemplates = useCallback(() => {
    api.get("/cv-templates").then(({ data }) => setSavedTemplates(data)).catch(() => {});
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const fetchPreview = useCallback(async () => {
    setPreviewBusy(true);
    try {
      const res = await api.get(`/resumes/${resumeId}/pdf?lang=nl`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch {
      // preview is best-effort; errors surface via the PDF download flows
    } finally {
      setPreviewBusy(false);
    }
  }, [resumeId]);

  const fetchAvailableRefs = useCallback(async () => {
    try {
      const { data } = await api.get("/references");
      setAvailableRefs((data || []).filter((r) => r.status === "approved"));
    } catch {
      setAvailableRefs((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setR(null);
    setDirty(false);
    api.get(`/resumes/${resumeId}`).then(({ data }) => {
      if (active) {
        // Sort experiences on-load so the editor form matches the WYSIWYG
        // PDF right away (server sorts too, but not all legacy CVs were
        // persisted sorted). Auto-sort only fires here on `resumeId` change,
        // so per-keystroke edits don't cause the row to jump mid-type.
        const sortedData = {
          ...data,
          experiences: sortExperiences(data.experiences || []),
          extracurricular: sortExperiences(data.extracurricular || []),
        };
        setR(sortedData);
        fetchPreview();
      }
    }).catch(() => toast.error(t("detailLoadFailed")));
    // Fetch the central profile once so we know which personalia chips
    // actually have data behind them — showing a "GEBOORTEDATUM" toggle
    // for a profile field that's empty is a UX trap.
    api.get("/profile/personal").then(({ data }) => {
      if (active) setProfileData(data?.personal || {});
    }).catch(() => { if (active) setProfileData({}); });
    // Approved references — powers the multi-select dropdown in the
    // "Referenties" section. Only approved refs are pickable, ordering
    // follows the user's selection order.
    fetchAvailableRefs();
    return () => { active = false; };
  }, [resumeId, t, fetchPreview, fetchAvailableRefs]);

  // Live-refresh the approved-references pool when the user comes back to
  // the tab or window. A referee editing their `share_fields` in another
  // tab (or on their phone) should be reflected in the live PDF preview
  // without the editor having to be reopened.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAvailableRefs();
    };
    window.addEventListener("focus", fetchAvailableRefs);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", fetchAvailableRefs);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchAvailableRefs]);

  // Revoke blob URL on unmount
  useEffect(() => () => {
    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
  }, []);

  const update = useCallback((patch) => {
    setR((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const design = r?.design || { ...PRESET_DESIGNS[r?.template || "classic"], photo_shape: "square", show_photo: true };
  const setDesign = (patch) => update({ design: { ...design, ...patch } });

  const setPersonal = (field, value) =>
    update({ personal: { ...r.personal, [field]: value } });

  const save = useCallback(async () => {
    const cur = rRef.current;
    if (!cur) return null;
    setSaving(true);
    try {
      const payload = {
        name: cur.name, personal: cur.personal, profile: cur.profile,
        template: cur.template || "classic",
        design: cur.design || null,
        personal_fields: cur.personal_fields || null,
        show_personal: cur.show_personal !== false,
        // Experiences saved as-is so manual DnD reorder wins. Initial
        // HEDEN-first sort is applied on-LOAD only (`fetchAvailableRefs`
        // effect above), so legacy CVs still get a nice default order.
        skills: cur.skills,
        experiences: cur.experiences || [],
        education: cur.education,
        extracurricular: cur.extracurricular || [],
        hobbies: cur.hobbies, references_text: cur.references_text,
        reference_ids: cur.reference_ids || [],
        portfolio_id: cur.portfolio_id || "",
        cover_portfolio_item_id: cur.cover_portfolio_item_id || "",
      };
      const { data } = await api.put(`/resumes/${resumeId}`, payload);
      // Only mark clean when no changes were made while the request was in flight
      if (rRef.current === cur) setDirty(false);
      onChanged?.();
      fetchPreview();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbSaveFailed"));
      return null;
    } finally {
      setSaving(false);
    }
  }, [resumeId, onChanged, t, fetchPreview]);

  // Auto-save: debounce 1.2s after the last change
  useEffect(() => {
    if (!dirty || !r) return;
    const timer = setTimeout(() => { save(); }, 1200);
    return () => clearTimeout(timer);
  }, [r, dirty, save]);

  const handleClose = useCallback(() => {
    if (rRef.current && dirty) save();
    onClose?.();
  }, [dirty, save, onClose]);

  // Escape closes the editor
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const downloadPdf = async (langs) => {
    setPdfBusy(true);
    try {
      if (dirty) {
        const saved = await save();
        if (!saved) return;
      }
      for (const pdfLang of langs) {
        const res = await api.get(`/resumes/${resumeId}/pdf?lang=${pdfLang}`, { responseType: "blob" });
        const safe = (rRef.current?.name || "cv").replace(/[^A-Za-z0-9_-]+/g, "_");
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `cv_${safe}_${pdfLang}.pdf`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbPdfFailed"));
    } finally {
      setPdfBusy(false);
    }
  };

  const refreshPreview = async () => {
    // Also re-hydrate references so any share-fields changes made by
    // referees since the editor was opened land in the WYSIWYG preview.
    fetchAvailableRefs();
    if (dirty) { await save(); } else { await fetchPreview(); }
  };

  const applyQuickTemplate = (val) => {
    if (val.startsWith("tpl:")) {
      const tpl = savedTemplates.find((x) => x.id === val.slice(4));
      if (tpl) { setDesign({ ...tpl.design }); setAppliedTplId(tpl.id); toast.success(t("rbTplApplied")); }
    } else if (val.startsWith("preset:")) {
      const key = val.slice(7);
      setAppliedTplId(null);
      update({ template: key, design: { ...design, ...PRESET_DESIGNS[key] } });
    }
  };

  const activePresetKey = !appliedTplId
    ? PRESET_TEMPLATES.find(({ key }) => PRESET_KEYS.every((k) => design[k] === PRESET_DESIGNS[key][k]))?.key
    : null;
  const quickTplValue = appliedTplId ? `tpl:${appliedTplId}` : activePresetKey ? `preset:${activePresetKey}` : "";

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if (!r.skills.some((x) => x.name === s)) update({ skills: [...r.skills, { name: s, level: 0 }] });
    setSkillInput("");
  };

  const setSkillLevel = (name, level) =>
    update({
      skills: r.skills.map((x) =>
        x.name === name ? { ...x, level: x.level === level ? 0 : level } : x
      ),
    });

  // Drag-and-drop reordering of the skill pills. Mirrors the References
  // section pattern: HTML5 DnD is enough for a short list (<20 items) and
  // avoids pulling in a DnD library. Top-of-list skills land higher on the
  // printed CV, so users can prioritise their strongest strengths.
  const moveSkill = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    if (fromIdx >= r.skills.length || toIdx >= r.skills.length) return;
    const next = [...r.skills];
    const [it] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, it);
    update({ skills: next });
  };

  const addHobby = () => {
    const h = hobbyInput.trim();
    if (!h) return;
    if (!r.hobbies.includes(h)) update({ hobbies: [...r.hobbies, h] });
    setHobbyInput("");
  };

  if (!r) {
    return (
      <div className="fixed inset-0 lg:left-72 z-50 bg-[#F9F8F6] flex items-center justify-center" data-testid="resume-editor">
        <p className="ed-eyebrow">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 lg:left-72 z-50 bg-[#F9F8F6] flex flex-col" data-testid="resume-editor">
      {/* Toolbar */}
      <div className="border-b border-neutral-200 px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3 shrink-0 bg-[#F9F8F6]">
        <button data-testid="resume-editor-back" className="ed-btn ed-btn-outline" onClick={handleClose}>
          <ArrowLeft size={14} strokeWidth={1.5} /> <span className="hidden sm:inline">{t("rbEditorBack")}</span>
        </button>
        <input
          data-testid="resume-name-input"
          className="ed-input max-w-[13rem] font-serif text-lg"
          value={r.name}
          onChange={(e) => update({ name: e.target.value })}
        />
        <div className="hidden md:flex items-center gap-2">
          <label className="ed-label !mb-0">{t("rbTplQuick")}</label>
          <select
            data-testid="resume-tpl-quick"
            className="ed-input !w-auto max-w-[12rem] font-mono text-xs"
            value={quickTplValue}
            onChange={(e) => applyQuickTemplate(e.target.value)}
          >
            <option value="" disabled>—</option>
            {savedTemplates.length > 0 && (
              <optgroup label={t("rbTplSavedTitle")}>
                {savedTemplates.map((tpl) => (
                  <option key={tpl.id} value={`tpl:${tpl.id}`}>{tpl.name}</option>
                ))}
              </optgroup>
            )}
            <optgroup label={t("rbTplPresets")}>
              {PRESET_TEMPLATES.map(({ key, labelKey }) => (
                <option key={key} value={`preset:${key}`}>{t(labelKey)}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <span
          data-testid="resume-autosave-status"
          className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 px-1 ${saving || dirty ? "text-neutral-500" : "text-emerald-700"}`}
        >
          {saving ? (
            <><RefreshCw size={11} strokeWidth={1.5} className="animate-spin" /> {t("rbAutosaving")}</>
          ) : dirty ? (
            t("rbAutosavePending")
          ) : (
            <><Check size={11} strokeWidth={2} /> {t("rbAutosaved")}</>
          )}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Mobile pane toggle */}
          <div className="flex lg:hidden border border-neutral-300" data-testid="resume-mobile-toggle">
            <button
              data-testid="resume-mobile-edit"
              onClick={() => setMobilePane("edit")}
              className={`flex items-center gap-1.5 px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${mobilePane === "edit" ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-600"}`}
            >
              <PenLine size={12} strokeWidth={1.5} /> {t("rbMobileEdit")}
            </button>
            <button
              data-testid="resume-mobile-preview"
              onClick={() => setMobilePane("preview")}
              className={`flex items-center gap-1.5 px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${mobilePane === "preview" ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-600"}`}
            >
              <Eye size={12} strokeWidth={1.5} /> {t("rbMobilePreview")}
            </button>
          </div>
          <div className="flex items-center border border-neutral-300 bg-white" data-testid="resume-pdf-lang-wrap">
            <label htmlFor="resume-pdf-lang" className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 pl-3 pr-2 shrink-0 hidden sm:inline">
              {t("rbPdfLang")}
            </label>
            <select
              id="resume-pdf-lang"
              data-testid="resume-pdf-lang"
              className="bg-transparent font-mono text-[0.65rem] uppercase tracking-[0.15em] px-2 py-2 focus:outline-none"
              value={pdfLang}
              onChange={(e) => setPdfLang(e.target.value)}
              disabled={pdfBusy}
            >
              <option value="nl">{t("rbLangNl")}</option>
              <option value="en">{t("rbLangEn")}</option>
              <option value="de">{t("rbLangDe")}</option>
              <option value="fr">{t("rbLangFr")}</option>
              <option value="es">{t("rbLangEs")}</option>
            </select>
          </div>
          <button data-testid="resume-pdf-download" className="ed-btn" onClick={() => downloadPdf([pdfLang])} disabled={pdfBusy}>
            <FileDown size={14} strokeWidth={1.5} /> {pdfBusy ? t("rbPdfGenerating") : t("rbPdfDownload")}
          </button>
        </div>
      </div>

      {/* Split body */}
      <div className="flex-1 flex min-h-0">
        {/* Left: live preview (collapsible on lg+) */}
        <div
          className={`${mobilePane === "preview" ? "flex" : "hidden"} ${previewCollapsed ? "lg:hidden" : "lg:flex"} flex-col w-full lg:w-1/2 border-r border-neutral-200 min-h-0`}
          data-testid="resume-live-preview"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-neutral-200 bg-[#F9F8F6]">
            <p className="ed-eyebrow flex items-center gap-2">
              <Eye size={11} strokeWidth={1.5} /> {t("rbLivePreview")}
            </p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[0.6rem] text-neutral-500 hidden xl:inline">{t("rbPreviewAutoHint")}</span>
              <button data-testid="resume-preview-refresh" className="ed-btn ed-btn-outline" onClick={refreshPreview} disabled={previewBusy}>
                <RefreshCw size={13} strokeWidth={1.5} className={previewBusy ? "animate-spin" : ""} /> {t("rbPreviewRefresh")}
              </button>
              <button
                data-testid="resume-preview-collapse"
                onClick={() => setPreviewCollapsed(true)}
                className="hidden lg:inline-flex ed-btn ed-btn-outline"
                title={t("rbPreviewHide")}
              >
                <EyeOff size={13} strokeWidth={1.5} /> {t("rbPreviewHide")}
              </button>
            </div>
          </div>
          {previewUrl ? (
            <iframe title="cv-preview" src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`} className="flex-1 w-full bg-neutral-300" data-testid="resume-preview-iframe" />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-neutral-200">
              <p className="ed-eyebrow">{t("rbPreviewBusy")}</p>
            </div>
          )}
        </div>

        {/* Right: editor */}
        <div className={`${mobilePane === "edit" ? "block" : "hidden"} lg:block flex-1 overflow-y-auto min-h-0`} data-testid="resume-editor-pane">
          {/* Show-preview toggle when collapsed (only visible on lg+) */}
          {previewCollapsed && (
            <div className="hidden lg:flex items-center justify-end px-5 sm:px-8 pt-4">
              <button
                data-testid="resume-preview-show"
                onClick={() => setPreviewCollapsed(false)}
                className="ed-btn ed-btn-outline"
                title={t("rbPreviewShow")}
              >
                <Eye size={13} strokeWidth={1.5} /> {t("rbPreviewShow")}
              </button>
            </div>
          )}
          <div className="px-5 sm:px-8 py-8 space-y-10 max-w-3xl">
            <p className="text-[0.65rem] font-mono text-neutral-500">{t("rbPdfHint")}</p>

            {/* AI-generated variant: transparent info-panel with a diff of
                the rewritten sections + grounded-in evidence per change. */}
            <AiGeneratedInfoPanel
              resumeId={resumeId}
              generatedFromJob={r.generated_from_job}
              meta={r.ai_rewrite_meta}
              onRevertProfile={r.ai_rewrite_meta?.profile ? () => {
                update({ profile: r.ai_rewrite_meta.profile.old || "" });
                toast.success(t("aiInfoReverted"));
              } : null}
              onRevertExperience={(expId, oldText) => {
                update({
                  experiences: (r.experiences || []).map((e) =>
                    e.id === expId ? { ...e, description: oldText || "" } : e
                  ),
                });
                toast.success(t("aiInfoReverted"));
              }}
            />

            {/* Template chooser */}
            <Section title={t("rbSecTemplate")} testId="resume-sec-template">
              <p className="text-xs font-mono text-neutral-500 mb-4">{t("rbTplPickHint")}</p>

              {savedTemplates.length > 0 && (
                <div className="mb-6 max-w-3xl" data-testid="resume-saved-templates">
                  <p className="ed-label mb-2">{t("rbTplSavedTitle")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {savedTemplates.map((tpl, i) => (
                      <button
                        key={tpl.id}
                        data-testid={`resume-tpl-apply-${i}`}
                        onClick={() => { setDesign({ ...tpl.design }); setAppliedTplId(tpl.id); toast.success(t("rbTplApplied")); }}
                        className={`ed-card p-4 text-left flex items-center gap-3 transition-colors ${appliedTplId === tpl.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                      >
                        <span className="w-6 h-6 shrink-0 border border-neutral-300" style={{ background: tpl.design?.accent || "#1A1A1A" }} />
                        <span className="font-serif text-lg truncate flex-1">{tpl.name}</span>
                        {appliedTplId === tpl.id && <Check size={15} strokeWidth={2} className="shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="ed-label mb-2">{t("rbTplPresets")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                {PRESET_TEMPLATES.map(({ key, labelKey, hintKey, color }) => {
                  const active = !appliedTplId && PRESET_KEYS.every((k) => design[k] === PRESET_DESIGNS[key][k]);
                  return (
                    <button
                      key={key}
                      data-testid={`resume-template-${key}`}
                      onClick={() => { setAppliedTplId(null); update({ template: key, design: { ...design, ...PRESET_DESIGNS[key] } }); }}
                      className={`ed-card p-4 text-left flex items-start gap-3 transition-colors ${active ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                    >
                      <span className="mt-0.5 w-6 h-6 shrink-0 border border-neutral-300" style={{ background: color }} />
                      <span className="min-w-0">
                        <span className="font-serif text-lg block leading-tight">{t(labelKey)}</span>
                        <span className="text-[0.65rem] font-mono text-neutral-500 block mt-0.5">{t(hintKey)}</span>
                      </span>
                      {active && <Check size={15} strokeWidth={2} className="ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <p className="text-[0.65rem] font-mono text-neutral-500 mt-4" data-testid="resume-tpl-manage-hint">{t("rbTplManageHint")}</p>
            </Section>

            {/* Personalia */}
            <Section title={t("rbSecPersonal")} testId="resume-sec-personal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                {PERSONAL_FIELDS.map(([field, labelKey]) => (
                  <div key={field}>
                    <label className="ed-label">{t(labelKey)}</label>
                    <input
                      data-testid={`resume-personal-${field}`}
                      className="ed-input"
                      value={r.personal[field] || ""}
                      onChange={(e) => setPersonal(field, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[0.65rem] font-mono text-neutral-500 mt-3" data-testid="resume-personal-profile-hint">{t("rbPersonalFromProfile")}</p>

              <div className="mt-5">
                <p className="ed-label mb-2">{t("rbPersonalFieldsLabel")}</p>
                {(() => {
                  // Only render chips for fields the user has actually
                  // filled in their central profile. `address` reads
                  // structured street/postal/city/country too so users who
                  // only filled the new fields still see the address chip.
                  const isFilled = (k) => {
                    if (!profileData) return true; // pre-load: show all
                    if (k === "address") {
                      return !!(profileData.address || profileData.address_street ||
                                profileData.postal_code || profileData.city || profileData.country);
                    }
                    return !!(profileData[k] || "").trim();
                  };
                  const visible = PERSONAL_TOGGLE_FIELDS.filter(([k]) => isFilled(k));
                  if (visible.length === 0) {
                    return (
                      <div
                        className="border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center"
                        data-testid="resume-personal-empty"
                      >
                        <p className="text-xs text-neutral-600 leading-relaxed">
                          {t("rbPersonalNoneFilled")}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="flex flex-wrap gap-2" data-testid="resume-personal-toggles">
                      {visible.map(([field, labelKey]) => {
                    const active = (r.personal_fields || ALL_PERSONAL_KEYS).includes(field);
                    return (
                      <button
                        key={field}
                        data-testid={`resume-pfield-${field}`}
                        onClick={() => {
                          const cur = r.personal_fields || ALL_PERSONAL_KEYS;
                          const next = active ? cur.filter((k) => k !== field) : [...cur, field];
                          update({ personal_fields: next });
                        }}
                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${active ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900"}`}
                      >
                        <span className={`w-3 h-3 rounded-full border flex items-center justify-center ${active ? "border-[#F9F8F6]" : "border-neutral-400"}`}>
                          {active && <Check size={9} strokeWidth={3} />}
                        </span>
                        {t(labelKey)}
                      </button>
                    );
                  })}
                    </div>
                  );
                })()}
                <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("rbPersonalFieldsHint")}</p>
              </div>
            </Section>

            {/* Profiel */}
            <Section title={t("rbSecProfile")} testId="resume-sec-profile">
              <PopoutTextarea
                dataTestId="resume-profile-textarea"
                className="min-h-[8rem]"
                placeholder={t("rbProfilePh")}
                value={r.profile}
                onChange={(next) => update({ profile: next })}
                title={t("rbSecProfile")}
                rows={5}
              />
              <button data-testid="resume-profile-wizard-btn" className="ed-btn mt-3" onClick={() => setProfileWizard(true)}>
                <Sparkles size={14} strokeWidth={1.5} /> {t("rbProfileWizard")}
              </button>
            </Section>

            {/* Vaardigheden */}
            <Section title={t("rbSecSkills")} testId="resume-sec-skills">
              <div className="flex flex-wrap gap-2 mb-2" data-testid="resume-skills-list">
                {r.skills.map((s, i) => (
                  <span
                    key={s.name}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                      if (!Number.isNaN(from)) moveSkill(from, i);
                    }}
                    data-testid={`resume-skill-pill-${i}`}
                    title={t("rbSkillDragHint")}
                    className="inline-flex items-center gap-2 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 cursor-grab active:cursor-grabbing select-none"
                  >
                    <span className="opacity-60">⋮⋮</span>
                    {s.name}
                    <span className="inline-flex items-center gap-[3px]" data-testid={`resume-skill-level-${i}`}>
                      {[1, 2, 3, 4, 5].map((lv) => (
                        <button
                          key={lv}
                          data-testid={`resume-skill-level-${i}-${lv}`}
                          onClick={() => setSkillLevel(s.name, lv)}
                          title={`${lv}/5`}
                          className={`w-2 h-2 rounded-full transition-colors ${lv <= (s.level || 0) ? "bg-[#F9F8F6]" : "bg-neutral-600 hover:bg-neutral-400"}`}
                        />
                      ))}
                    </span>
                    <button
                      data-testid={`resume-skill-remove-${s.name}`}
                      onClick={() => update({ skills: r.skills.filter((x) => x.name !== s.name) })}
                      className="hover:text-rose-300"
                    >
                      <X size={11} strokeWidth={2} />
                    </button>
                  </span>
                ))}
              </div>
              <p className="text-[0.65rem] font-mono text-neutral-500 mb-3">{t("rbSkillLevelHint")}</p>
              <div className="flex gap-2 max-w-md">
                <input
                  data-testid="resume-skill-input"
                  className="ed-input"
                  placeholder={t("rbSkillPh")}
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                />
                <button data-testid="resume-skill-add" className="ed-btn ed-btn-outline shrink-0" onClick={addSkill}>
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
            </Section>

            {/* Werkervaring */}
            <Section title={t("rbSecExperience")} testId="resume-sec-experience">
              <ExperienceList
                items={r.experiences}
                onChange={(items) => update({ experiences: items })}
                addLabelKey="rbAddExperience"
                testPrefix="resume-exp"
              />
            </Section>

            {/* Opleiding */}
            <Section title={t("rbSecEducation")} testId="resume-sec-education">
              <div className="space-y-3">
                {r.education.map((edu, i) => (
                  <div key={edu.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_7rem_6rem_auto] gap-3 items-end">
                    <div>
                      <label className="ed-label">{t("rbEduTitle")}</label>
                      <input data-testid={`resume-edu-title-${i}`} className="ed-input" value={edu.title}
                        onChange={(e) => update({ education: r.education.map((x) => x.id === edu.id ? { ...x, title: e.target.value } : x) })} />
                    </div>
                    <div>
                      <label className="ed-label">{t("rbEduInstitution")}</label>
                      <input className="ed-input" value={edu.institution}
                        onChange={(e) => update({ education: r.education.map((x) => x.id === edu.id ? { ...x, institution: e.target.value } : x) })} />
                    </div>
                    <div>
                      <label className="ed-label">{t("rbEduLevel")}</label>
                      <input data-testid={`resume-edu-level-${i}`} className="ed-input" placeholder={t("rbEduLevelPh")} value={edu.level || ""}
                        onChange={(e) => update({ education: r.education.map((x) => x.id === edu.id ? { ...x, level: e.target.value } : x) })} />
                    </div>
                    <div>
                      <label className="ed-label">{t("rbEduYear")}</label>
                      <input className="ed-input" value={edu.year}
                        onChange={(e) => update({ education: r.education.map((x) => x.id === edu.id ? { ...x, year: e.target.value } : x) })} />
                    </div>
                    <button
                      data-testid={`resume-edu-delete-${i}`}
                      onClick={() => update({ education: r.education.filter((x) => x.id !== edu.id) })}
                      className="p-2.5 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
                    >
                      <X size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
                <button data-testid="resume-edu-add" className="ed-btn ed-btn-outline"
                  onClick={() => update({ education: [...r.education, newEducation()] })}>
                  <Plus size={14} strokeWidth={1.5} /> {t("rbAddEducation")}
                </button>
              </div>
            </Section>

            {/* Extracurriculair */}
            <Section title={t("rbSecExtra")} testId="resume-sec-extra">
              <ExperienceList
                items={r.extracurricular}
                onChange={(items) => update({ extracurricular: items })}
                addLabelKey="rbAddExtra"
                testPrefix="resume-extra"
              />
            </Section>

            {/* Hobby's */}
            <Section title={t("rbSecHobbies")} testId="resume-sec-hobbies">
              <div className="flex flex-wrap gap-2 mb-3">
                {(r.hobbies || []).map((h) => (
                  <span key={h} className="inline-flex items-center gap-2 border border-neutral-300 bg-white font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5">
                    {h}
                    <button
                      data-testid={`resume-hobby-remove-${h}`}
                      onClick={() => update({ hobbies: r.hobbies.filter((x) => x !== h) })}
                      className="hover:text-rose-700"
                    >
                      <X size={11} strokeWidth={2} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 max-w-md">
                <input
                  data-testid="resume-hobby-input"
                  className="ed-input"
                  placeholder={t("rbHobbyPh")}
                  value={hobbyInput}
                  onChange={(e) => setHobbyInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHobby(); } }}
                />
                <button data-testid="resume-hobby-add" className="ed-btn ed-btn-outline shrink-0" onClick={addHobby}>
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
            </Section>

            {/* Portfolio-QR — koppel deze CV aan een portfolio zodat er
                automatisch een QR-code op de PDF verschijnt naar de
                openbare showcase. Optioneel: kies ook één project als
                'featured work' pagina achterop de CV. */}
            <PortfolioQrPicker
              currentId={r.portfolio_id || ""}
              coverItemId={r.cover_portfolio_item_id || ""}
              onChange={(pid) => update({ portfolio_id: pid, cover_portfolio_item_id: "" })}
              onChangeCover={(iid) => update({ cover_portfolio_item_id: iid })}
            />

            {/* Referenties */}
            <Section title={t("rbSecReferences")} testId="resume-sec-references">
              {(() => {
                const selectedIds = r.reference_ids || [];
                const selectedRefs = selectedIds
                  .map((id) => (availableRefs || []).find((x) => x.id === id))
                  .filter(Boolean);
                const remaining = (availableRefs || []).filter((x) => !selectedIds.includes(x.id));
                const toggle = (id) => {
                  const cur = new Set(selectedIds);
                  cur.has(id) ? cur.delete(id) : cur.add(id);
                  update({ reference_ids: Array.from(cur) });
                };
                // Drag-and-drop reordering of the selected pills. HTML5 DnD
                // is sufficient here — the list is tiny (usually <5 items)
                // and pulling in react-beautiful-dnd for this would balloon
                // the bundle. State stored in refs (below) for immediacy.
                const moveTo = (fromIdx, toIdx) => {
                  if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
                  const next = [...selectedIds];
                  const [it] = next.splice(fromIdx, 1);
                  next.splice(toIdx, 0, it);
                  update({ reference_ids: next });
                };
                // Live PDF-preview of the compiled references block —
                // mirrors backend `_compile_references_text` verbatim so
                // users see exactly what will land on the printed CV.
                const formatRef = (ref) => {
                  const share = new Set(ref.share_fields || []);
                  const parts = [];
                  if (share.has("name") && ref.name) parts.push(ref.name);
                  if (share.has("role_title") && ref.role_title) parts.push(ref.role_title);
                  if (share.has("company") && ref.company) parts.push(ref.company);
                  if (share.has("email") && ref.email) parts.push(ref.email);
                  if (share.has("phone") && ref.phone) parts.push(ref.phone);
                  if (share.has("years_known") && ref.years_known) parts.push(`(${ref.years_known})`);
                  return parts.join(" · ");
                };
                const previewLines = selectedRefs.map(formatRef).filter(Boolean);
                const previewText = previewLines.length
                  ? previewLines.join("\n")
                  : t("rbRefPreviewDefault");

                return (
                  <div className="space-y-3" data-testid="resume-references-picker">
                    {/* Selected pills — draggable to reorder; × to remove */}
                    {selectedRefs.length > 0 && (
                      <div className="flex flex-wrap gap-2" data-testid="resume-references-selected">
                        {selectedRefs.map((ref, idx) => (
                          <span
                            key={ref.id}
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(idx)); e.dataTransfer.effectAllowed = "move"; }}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                              if (!Number.isNaN(from)) moveTo(from, idx);
                            }}
                            data-testid={`resume-reference-pill-${ref.id}`}
                            className="inline-flex items-center gap-2 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] font-mono text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 cursor-grab active:cursor-grabbing select-none"
                            title={t("rbRefDragHint")}
                          >
                            <span className="opacity-60">⋮⋮</span>
                            {ref.name}
                            <button
                              type="button"
                              data-testid={`resume-reference-remove-${ref.id}`}
                              onClick={() => toggle(ref.id)}
                              className="text-neutral-400 hover:text-white"
                              aria-label={t("rbRefRemove")}
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Dropdown trigger + panel */}
                    <div className="relative max-w-lg">
                      <button
                        type="button"
                        data-testid="resume-references-toggle"
                        onClick={() => setRefPickerOpen((v) => !v)}
                        className="ed-input w-full text-left flex items-center justify-between gap-3"
                        aria-expanded={refPickerOpen}
                      >
                        <span className={selectedRefs.length ? "text-neutral-900" : "text-neutral-500"}>
                          {selectedRefs.length
                            ? t("rbRefSelectedCount", { n: selectedRefs.length })
                            : t("rbRefPickPh")}
                        </span>
                        <span className="font-mono text-xs text-neutral-500">{refPickerOpen ? "▲" : "▼"}</span>
                      </button>
                      {refPickerOpen && (
                        <div className="absolute left-0 right-0 mt-1 z-20 ed-card p-2 max-h-64 overflow-y-auto space-y-1" data-testid="resume-references-menu">
                          {availableRefs === null && (
                            <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500 px-2 py-2">{t("rbRefLoading")}</p>
                          )}
                          {availableRefs && availableRefs.length === 0 && (
                            <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500 px-2 py-2" data-testid="resume-references-empty">{t("rbRefEmpty")}</p>
                          )}
                          {(availableRefs || []).map((ref) => {
                            const active = selectedIds.includes(ref.id);
                            return (
                              <button
                                key={ref.id}
                                type="button"
                                data-testid={`resume-reference-option-${ref.id}`}
                                onClick={() => toggle(ref.id)}
                                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors ${active ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"}`}
                              >
                                <span>
                                  <span className="font-serif text-sm block">{ref.name}</span>
                                  {ref.role_title && <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] block opacity-70">{ref.role_title}{ref.company ? ` · ${ref.company}` : ""}</span>}
                                </span>
                                <span className="font-mono text-[0.7rem]">{active ? "✓" : ""}</span>
                              </button>
                            );
                          })}
                          {remaining.length === 0 && selectedRefs.length > 0 && availableRefs && availableRefs.length > 0 && (
                            <p className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 px-2 pt-2 border-t border-neutral-200">{t("rbRefAllPicked")}</p>
                          )}
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-neutral-500" data-testid="resume-references-hint">
                      {selectedRefs.length === 0 ? t("rbRefDefault") : t("rbRefSelectedHint")}
                    </p>

                    {/* Live PDF preview — mirrors what recruiters will read */}
                    <div className="border border-dashed border-neutral-300 bg-neutral-50 p-3" data-testid="resume-references-preview">
                      <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-1">
                        {t("rbRefPreviewEyebrow")}
                      </p>
                      <pre className="font-serif text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed m-0" data-testid="resume-references-preview-text">
                        {previewText}
                      </pre>
                    </div>
                  </div>
                );
              })()}
            </Section>
          </div>
        </div>
      </div>

      {profileWizard && (
        <ProfileWizard
          experiences={r.experiences}
          onClose={() => setProfileWizard(false)}
          onSave={(text) => { update({ profile: text }); setProfileWizard(false); }}
        />
      )}
    </div>
  );
}

/**
 * PortfolioQrPicker — pick a portfolio to attach as a QR-code on the CV
 * (bottom-left), plus optionally a single project to append as a
 * 'featured work' magazine spread after the last resume page. Renders a
 * live QR preview so the user can see exactly what recruiters will scan.
 */
function PortfolioQrPicker({ currentId, coverItemId, onChange, onChangeCover }) {
  const { t } = useT();
  const [portfolios, setPortfolios] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsBusy, setItemsBusy] = useState(false);
  const [qrCacheKey, setQrCacheKey] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get("/portfolios").then(({ data }) => {
      if (alive) setPortfolios(data?.items || []);
    }).catch(() => { if (alive) setPortfolios([]); });
    return () => { alive = false; };
  }, []);

  // Load items when portfolio picked so the cover-item picker can list them.
  useEffect(() => {
    if (!currentId) { setItems([]); return; }
    let alive = true;
    setItemsBusy(true);
    api.get(`/portfolios/${currentId}`).then(({ data }) => {
      if (alive) setItems(data?.items || []);
    }).catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setItemsBusy(false); });
    return () => { alive = false; };
  }, [currentId]);

  // Bust the QR-preview cache so re-picking the same portfolio still
  // refreshes visually (e.g. after the slug changed under the hood).
  useEffect(() => { setQrCacheKey((k) => k + 1); }, [currentId]);

  if (portfolios === null) return null;

  const qrPreviewUrl = currentId
    ? `${process.env.REACT_APP_BACKEND_URL}/api/portfolios/${currentId}/qr.png?ts=${qrCacheKey}`
    : "";

  return (
    <Section title={t("rbSecPortfolioQr")} testId="resume-sec-portfolio-qr">
      {portfolios.length === 0 ? (
        <p className="text-xs text-neutral-500 italic leading-relaxed">
          {t("rbPortfolioQrEmpty")}
        </p>
      ) : (
        <>
          <p className="text-xs text-neutral-600 leading-relaxed mb-2">
            {t("rbPortfolioQrHelp")}
          </p>
          <div className="flex flex-wrap gap-2" data-testid="resume-portfolio-qr-list">
            <button
              type="button"
              data-testid="resume-portfolio-qr-none"
              onClick={() => onChange("")}
              className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                !currentId ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "text-neutral-500 border-neutral-300 hover:border-neutral-900"
              }`}
            >
              {t("rbPortfolioQrNone")}
            </button>
            {portfolios.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`resume-portfolio-qr-pick-${p.slug}`}
                onClick={() => onChange(p.id)}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                  currentId === p.id ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "text-neutral-700 border-neutral-300 hover:border-neutral-900"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
          {currentId && (
            <div className="mt-4 border border-neutral-200 bg-neutral-50 p-3 flex items-start gap-3" data-testid="resume-portfolio-qr-preview">
              <img
                src={qrPreviewUrl}
                alt="QR preview"
                className="w-24 h-24 bg-white border border-neutral-200"
                onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
              />
              <div className="flex-1 min-w-0 text-xs text-neutral-700 leading-relaxed">
                <p className="font-mono uppercase tracking-[0.15em] text-[0.6rem] text-neutral-500 mb-1">
                  {t("rbPortfolioQrPreview")}
                </p>
                <p>{t("rbPortfolioQrPreviewBody")}</p>
              </div>
            </div>
          )}

          {currentId && (
            <div className="mt-5 border-t border-neutral-100 pt-4">
              <p className="ed-label">{t("rbCoverItem")}</p>
              <p className="text-xs text-neutral-600 leading-relaxed mb-2">
                {t("rbCoverItemHelp")}
              </p>
              {itemsBusy ? (
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">…</p>
              ) : items.length === 0 ? (
                <p className="text-xs text-neutral-500 italic">{t("rbCoverItemEmpty")}</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="resume-cover-item-list">
                  <button
                    type="button"
                    data-testid="resume-cover-item-none"
                    onClick={() => onChangeCover("")}
                    className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                      !coverItemId ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "text-neutral-500 border-neutral-300 hover:border-neutral-900"
                    }`}
                  >
                    {t("rbCoverItemNone")}
                  </button>
                  {items.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      data-testid={`resume-cover-item-pick-${it.id}`}
                      onClick={() => onChangeCover(it.id)}
                      className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors max-w-[220px] truncate ${
                        coverItemId === it.id ? "bg-amber-500 text-neutral-900 border-amber-500" : "text-neutral-700 border-neutral-300 hover:border-neutral-900"
                      }`}
                      title={it.project_name}
                    >
                      {it.project_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
